import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";

import APIHelper from "../utils/APIHelper";
import ProductForm from "../ProductForm";
import EmptyState from "../components/discovery/EmptyState";
import ContextualHint from "../components/discovery/ContextualHint";


export default function ProductsPage({ token }) {
  const [showForm, setShowForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [isMock, setIsMock] = useState(false);
  const [mockProducts, setMockProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const [editingProduct, setEditingProduct] = useState(null);

  const fetchProducts = async () => {
    try {
    const res = await APIHelper.listProducts(token, {
      q: query,
        status: statusFilter === "All" ? undefined : statusFilter,
      page: page + 1,
      limit: rowsPerPage,
      sortBy,
      sortDir,
    });
      const items = res.items || [];
      setProducts(items);
    setTotal(res.total || 0);
      
      // Show mock data if no products exist
      if (!items || items.length === 0 && page === 0 && !query) {
        const mocks = [
          {
            _id: "mock-1",
            sku: "PROD-001",
            title: "Sample Product 1",
            price: 29.99,
            status: "Active",
          },
          {
            _id: "mock-2",
            sku: "PROD-002",
            title: "Sample Product 2",
            price: 49.99,
            status: "Draft",
          },
          {
            _id: "mock-3",
            sku: "PROD-003",
            title: "Sample Product 3",
            price: 19.99,
            status: "Active",
          },
        ];
        setMockProducts(mocks);
        setIsMock(true);
      } else {
        setIsMock(false);
        setMockProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setErrorMessage("Failed to fetch products");
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, rowsPerPage, sortBy, sortDir, statusFilter, query]);

  const statusChip = (status) => (
    <Chip
      label={status}
      size="small"
      className={`text-xs font-semibold ${
        status === "Active"
          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
      }`}
    />
  );

  // Server-side pagination/sorting: table renders current page data
  // Use mock products if no real products exist
  const displayProducts = isMock ? mockProducts : products;
  const paged = displayProducts;

  const handleSort = (column) => {
    const mapped = column === "name" ? "title" : column;
    if (sortBy === mapped) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(mapped);
      setSortDir("asc");
    }
    setPage(0);
  };

  if (showForm) {
    return (
      <div>
        <div className="mb-2">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setShowForm(false);
              setEditingProduct(null);
            }}
            className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
          >
            Back
          </Button>
        </div>
        <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {editingProduct ? "Edit Product" : "Add New Product"}
        </Typography>
        <div className="mt-4">
          <ProductForm
          key={editingProduct?._id || "new"}
          token={token}
          initialData={editingProduct}
          onProductAdded={() => {
            setShowForm(false);
            setEditingProduct(null);
            fetchProducts();
          }}
        />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className={`flex items-center justify-between ${isMock || errorMessage ? 'mb-0' : 'mb-2'}`}>
        <div className="flex items-center gap-2">
          <StorefrontIcon className="text-primary-600 dark:text-primary-400" />
          <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
            Products
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => {
            setEditingProduct(null);
            setShowForm(true);
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
        >
          Add Product
        </Button>
      </div>
      
      <ContextualHint
        id="products-page-hint"
        variant="info"
        message="💡 Pro Tip: Create products here and then link them to your Instagram posts to automate sales in one click!"
      />

      {isMock && (

        <div className="mt-2 mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
          <Typography className="text-sm text-primary-800 dark:text-primary-200">
            ℹ️ Showing mock products as placeholders. This will disappear once you
            add a real product.
          </Typography>
        </div>
      )}
      {errorMessage && (
        <div className="mt-2 mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center justify-between">
            <Typography className="text-sm text-red-800 dark:text-red-200">
              ⚠️ {errorMessage}
            </Typography>
            <button
              onClick={() => setErrorMessage("")}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Products table */}
      <div className="card-modern p-6 mt-2">

        <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Items ({isMock ? mockProducts.length : total})
        </Typography>
        
        {paged.length === 0 && !isMock ? (
          <EmptyState
            icon="🛍️"
            title="No products yet"
            description="Create your first product to link it to your Instagram posts and start selling! You can add images, price, and SKU for each product."
            ctaText="Create Product"
            ctaAction={() => setShowForm(true)}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="md:col-span-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Search SKU / Name"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(0);
                  }}
                  className="bg-white dark:bg-gray-800"
                />
              </div>
              <div>
                <FormControl fullWidth size="small">
                  <InputLabel id="prod-status-label" className="text-gray-700 dark:text-gray-300">Status</InputLabel>
                  <Select
                    labelId="prod-status-label"
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    className="bg-white dark:bg-gray-800"
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Draft">Draft</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className="overflow-x-auto">
              <TableContainer>
                <Table size="small" className="min-w-[650px]">
                  <TableHead>
                    <TableRow className="bg-gray-50 dark:bg-gray-800">
                      <TableCell className="text-gray-900 dark:text-white font-semibold">
                        <TableSortLabel
                          active={sortBy === "sku"}
                          direction={sortBy === "sku" ? sortDir : "asc"}
                          onClick={() => handleSort("sku")}
                          className="text-gray-900 dark:text-white"
                        >
                          SKU
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className="text-gray-900 dark:text-white font-semibold">
                        <TableSortLabel
                          active={sortBy === "title"}
                          direction={sortBy === "title" ? sortDir : "asc"}
                          onClick={() => handleSort("title")}
                          className="text-gray-900 dark:text-white"
                        >
                          Title
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" className="text-gray-900 dark:text-white font-semibold">
                        <TableSortLabel
                          active={sortBy === "price"}
                          direction={sortBy === "price" ? sortDir : "asc"}
                          onClick={() => handleSort("price")}
                          className="text-gray-900 dark:text-white"
                        >
                          Price
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className="text-gray-900 dark:text-white font-semibold">
                        <TableSortLabel
                          active={sortBy === "status"}
                          direction={sortBy === "status" ? sortDir : "asc"}
                          onClick={() => handleSort("status")}
                          className="text-gray-900 dark:text-white"
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className="text-gray-900 dark:text-white font-semibold">Sale Link</TableCell>
                      <TableCell align="right" className="text-gray-900 dark:text-white font-semibold">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((p) => (
                      <TableRow key={p._id || p.sku} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <TableCell className="text-gray-900 dark:text-white">{p.sku}</TableCell>
                        <TableCell className="text-gray-900 dark:text-white">{p.title}</TableCell>
                        <TableCell align="right" className="text-gray-900 dark:text-white">
                          ₹{Number(p.price).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {isMock || p._id?.startsWith("mock-") ? (
                            statusChip(p.status)
                          ) : (
                            <FormControl size="small" className="min-w-[120px]">
                              <Select
                                value={p.status}
                                onChange={async (e) => {
                                  try {
                                    await APIHelper.updateProduct(token, p._id, {
                                      status: e.target.value,
                                    });
                                    fetchProducts();
                                  } catch (err) {
                                    setErrorMessage(
                                      err?.response?.data?.message || err.message
                                    );
                                  }
                                }}
                                className="bg-white dark:bg-gray-800"
                              >
                                <MenuItem value="Draft">Draft</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {isMock || p._id?.startsWith("mock-") ? (
                            <span className="text-gray-400 text-xs italic">Unavailable for mock data</span>
                          ) : (
                            <Tooltip title="Click to copy direct checkout link" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const link = `${window.location.origin}/checkout/prod_${p._id}`;
                                  navigator.clipboard.writeText(link);
                                  toast.success("Checkout link copied to clipboard!");
                                }}
                                color="primary"
                                className="hover:bg-primary-50 dark:hover:bg-primary-950/20"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isMock || p._id?.startsWith("mock-") ? (
                            <div className="text-gray-500 dark:text-gray-400 text-sm">
                              Mock data
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  setEditingProduct(p);
                                  setShowForm(true);
                                }}
                                className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case"
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={async () => {
                                  if (!window.confirm("Delete this product?")) return;
                                  try {
                                    await APIHelper.deleteProduct(token, p._id);
                                    fetchProducts();
                                  } catch (err) {
                                    setErrorMessage(
                                      err?.response?.data?.message || err.message
                                    );
                                  }
                                }}
                                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 normal-case"
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </>
        )}
        {!isMock && (
          <div className="mt-4">
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => {
                setPage(p);
                fetchProducts();
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
                fetchProducts();
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              className="text-gray-700 dark:text-gray-300"
            />
          </div>
        )}
      </div>

    </div>
  );
}
